import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { FlightKey } from "@b2bportal/air-price-watch-api";
import { useModuleBEM } from "@b2bportal/core-themes";
import {
  Dialog,
  DialogContent,
  type DialogProps,
  DialogTrigger,
} from "@b2bportal/core-ui/overridable";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";

import {
  useWatchTripDialog,
  WatchTripDialogView,
} from "./hooks/useWatchTripDialog";
import styles from "./styles.module.scss";

import { UnwatchTripFooter } from "./UnwatchTripFooter";
import { UnwatchTripView } from "./UnwatchTripView";
import { WatchTripFailure } from "./WatchTripFailure";
import { WatchTripFailureFooter } from "./WatchTripFailureFooter";
import { WatchTripForm, type WatchTripFormState } from "./WatchTripForm";
import { WatchTripFormFooter } from "./WatchTripFormFooter";
import { WatchTripSuccess } from "./WatchTripSuccess";
import { WatchTripSuccessFooter } from "./WatchTripSuccessFooter";
import { TenantIconNameEnum } from "@/types/tenantIcons";

export interface WatchTripDialogProps extends DialogProps {
  className?: string;
  flightKey: FlightKey;
  onSuccessClose?: () => void;
}

export const WatchTripDialog = ({
  className,
  open,
  onClose: propsOnClose,
  onOpen,
  children,
  flightKey,
  onSuccessClose,
}: WatchTripDialogProps): JSX.Element => {
  const { t } = useI18nContext();

  const [block, cn] = useModuleBEM(styles, "WatchTripDialog");

  const {
    context: { currentView },
    handlers: { onClose, navigate },
  } = useWatchTripDialog({
    open,
    onClose: propsOnClose,
  });

  const [watchFormState, setWatchFormState] = useState<WatchTripFormState>({
    isValid: false,
    isSubmitting: false,
  });
  const [unwatchSubmitting, setUnwatchSubmitting] = useState(false);

  const handleSuccessClose = useCallback(() => {
    onSuccessClose?.();
    onClose?.();
  }, [onSuccessClose, onClose]);

  // Display correct view
  let content: ReactNode = null;

  switch (currentView) {
    case WatchTripDialogView.WATCH:
      content = (
        <WatchTripForm
          cn={cn}
          flightKey={flightKey}
          onSuccess={() => {
            navigate(WatchTripDialogView.WATCH_SUCCESS);
          }}
          onError={() => {
            navigate(WatchTripDialogView.FAILURE);
          }}
          onFormStateChange={setWatchFormState}
        />
      );
      break;
    case WatchTripDialogView.WATCH_SUCCESS:
      content = <WatchTripSuccess cn={cn} />;
      break;
    case WatchTripDialogView.UNWATCH:
      content = (
        <UnwatchTripView
          cn={cn}
          flightKey={flightKey}
          onSuccess={onClose}
          onError={() => {
            navigate(WatchTripDialogView.FAILURE);
          }}
          onSubmittingChange={setUnwatchSubmitting}
        />
      );
      break;
    case WatchTripDialogView.FAILURE:
      content = <WatchTripFailure cn={cn} />;
      break;
  }

  // Bottom fixed buttons based on current view
  let bottomContent: ReactNode = null;

  switch (currentView) {
    case WatchTripDialogView.WATCH:
      bottomContent = (
        <WatchTripFormFooter
          cn={cn}
          formState={watchFormState}
          onCancel={onClose}
        />
      );
      break;
    case WatchTripDialogView.WATCH_SUCCESS:
      bottomContent = (
        <WatchTripSuccessFooter cn={cn} onClose={handleSuccessClose} />
      );
      break;
    case WatchTripDialogView.UNWATCH:
      bottomContent = (
        <UnwatchTripFooter cn={cn} isSubmitting={unwatchSubmitting} />
      );
      break;
    case WatchTripDialogView.FAILURE:
      bottomContent = <WatchTripFailureFooter cn={cn} onClose={onClose} />;
      break;
  }

  // Custom header with icon and title
  // The h2 element provides semantic structure for screen readers
  const customHeader = useMemo(
    () => (
      <div className={cn("custom-header")}>
        <div className={cn("icon-header")}>
          <Icon iconName={TenantIconNameEnum.binoculars} useCurrentColor />
        </div>
        {/* Radix DialogPrimitive.Title (rendered by DialogContent's title prop) already
            provides the h2; this must be a non-heading to avoid nested <h2>. */}
        <span className={cn("header-title")}>
          {t("priceWatch.titles.header")}
        </span>
      </div>
    ),
    [cn, t]
  );

  return (
    <Dialog open={open} onClose={onClose} onOpen={onOpen}>
      <DialogTrigger asChild>
        <button className={className}>{children}</button>
      </DialogTrigger>

      <DialogContent
        className={block}
        title={customHeader}
        bottomFixedContent={<div className={cn("footer")}>{bottomContent}</div>}
      >
        {content}
      </DialogContent>
    </Dialog>
  );
};
